import type { Lead, LeadStage } from '@coachapp/shared';
import { ChevronDown, Mail, Phone, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorAlert from './ErrorAlert';
import ClientCredentialsBlock from './ClientCredentialsBlock';
import Modal from './Modal';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import {
  useConvertLeadMutation,
  useGetLeadsQuery,
  useUpdateLeadMutation,
} from '../store/coachApi';

const STAGES: LeadStage[] = ['INQUIRY', 'TRIAL', 'CONVERTED', 'LOST'];

const STAGE_COLORS: Record<LeadStage, string> = {
  INQUIRY: 'bg-primary-soft text-primary',
  TRIAL: 'bg-amber-500/10 text-amber-700',
  CONVERTED: 'bg-emerald-500/10 text-emerald-700',
  LOST: 'bg-slate-100 text-slate-600',
};

const LeadsPanel = ({ searchQuery }: { searchQuery: string }) => {
  const { t } = useTranslation();
  const navigate = useTransitionNavigate();
  const { data: leads = [], error, isLoading, refetch } = useGetLeadsQuery();
  const [updateLead] = useUpdateLeadMutation();
  const [convertLead] = useConvertLeadMutation();
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [convertedCredentials, setConvertedCredentials] = useState<{
    login: string;
    password: string;
  } | null>(null);
  const [stageMenuId, setStageMenuId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchError = error ? getApiErrorMessage(error, t('Не удалось загрузить потенциальных клиентов')) : null;
  const loading = isLoading;

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleLeads = normalizedQuery
    ? leads.filter((lead) =>
        [lead.name, lead.email, lead.phone]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(normalizedQuery),
      )
    : leads;

  const openEdit = (lead: Lead) => {
    navigate(`/leads/${lead.id}/edit`, { state: { lead } });
  };

  const handleStageChange = async (lead: Lead, stage: LeadStage) => {
    setStageMenuId(null);
    if (lead.stage === stage) return;

    try {
      await updateLead({ id: lead.id, data: { stage } }).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    }
  };

  const handleConvert = async (lead: Lead) => {
    setConvertingId(lead.id);
    try {
      const result = await convertLead(lead.id).unwrap();
      if (result.client.login && result.password) {
        setConvertedCredentials({
          login: result.client.login,
          password: result.password,
        });
      }
    } catch (err) {
      setActionError(getApiErrorMessage(err, t('Не удалось конвертировать потенциального клиента')));
    } finally {
      setConvertingId(null);
    }
  };

  const leadsByStage = (stage: LeadStage) => visibleLeads.filter((l) => l.stage === stage);

  const truncateNotes = (notes?: string) => {
    if (!notes) return null;
    return notes.length > 60 ? `${notes.slice(0, 60)}…` : notes;
  };

  return (
    <>
      {fetchError && (
        <ErrorAlert
          message={fetchError}
          onRetry={() => void refetch()}
          onDismiss={() => {}}
        />
      )}
      {actionError && (
        <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />
      )}

      {loading ? (
        <p className="text-app-muted font-medium">{t('Загрузка...')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAGES.map((stage) => (
            <div key={stage} className="flex flex-col min-h-[200px]">
              <div
                className={`rounded-2xl px-3 py-2 mb-3 text-center text-xs font-semibold ${STAGE_COLORS[stage]}`}
              >
                {t(`leadStage.${stage}`)}
                <span className="ml-1.5 opacity-70">({leadsByStage(stage).length})</span>
              </div>
              <div className="flex flex-col gap-3 flex-1">
                {leadsByStage(stage).map((lead) => (
                  <div
                    key={lead.id}
                    className={`card relative p-4 space-y-2 cursor-pointer ${
                      stageMenuId === lead.id ? 'z-30' : ''
                    }`}
                    onClick={() => openEdit(lead)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-slate-900 leading-tight truncate">{lead.name}</p>
                      <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-ghost p-1.5 -mr-1"
                          onClick={() => setStageMenuId(stageMenuId === lead.id ? null : lead.id)}
                          aria-label={t('Изменить этап')}
                        >
                          <ChevronDown size={16} />
                        </button>
                        {stageMenuId === lead.id && (
                          <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl shadow-card py-1 min-w-[140px]">
                            {STAGES.map((s) => (
                              <button
                                key={s}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                                  lead.stage === s ? 'font-semibold text-primary' : 'text-slate-700'
                                }`}
                                onClick={() => handleStageChange(lead, s)}
                              >
                                {t(`leadStage.${s}`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-xs font-medium text-app-muted">{t(`leadSource.${lead.source}`)}</p>

                    {(lead.email || lead.phone) && (
                      <div className="flex flex-col gap-1 text-xs text-slate-600">
                        {lead.email && (
                          <span className="flex items-center gap-1.5">
                            <Mail size={12} className="shrink-0 text-slate-400" />
                            {lead.email}
                          </span>
                        )}
                        {lead.phone && (
                          <span className="flex items-center gap-1.5">
                            <Phone size={12} className="shrink-0 text-slate-400" />
                            {lead.phone}
                          </span>
                        )}
                      </div>
                    )}

                    {lead.notes && (
                      <p className="text-xs text-slate-500 leading-relaxed">{truncateNotes(lead.notes)}</p>
                    )}

                    {lead.stage === 'TRIAL' && (
                      <button
                        type="button"
                        className="btn-primary w-full text-xs py-2 mt-1"
                        disabled={convertingId === lead.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleConvert(lead);
                        }}
                      >
                        <UserCheck size={14} />
                        <span>{convertingId === lead.id ? t('Отправка...') : t('Конвертировать')}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {convertedCredentials && (
        <Modal onClose={() => setConvertedCredentials(null)} panelClassName="sm:max-w-md">
          <div className="modal-header mb-4">
            <h2 className="text-xl font-bold text-slate-900">{t('Клиент создан')}</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            {t('Отправьте клиенту логин и пароль для входа в мобильное приложение')}
          </p>
          <ClientCredentialsBlock
            login={convertedCredentials.login}
            password={convertedCredentials.password}
          />
          <button
            type="button"
            className="btn-primary w-full mt-6 modal-actions"
            onClick={() => setConvertedCredentials(null)}
          >
            {t('Готово')}
          </button>
        </Modal>
      )}
    </>
  );
};

export default LeadsPanel;
